<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { isValid, parse } from "chartjs-graph";
import useDetectOutsideClick from "@/Utils/useDetectOutsideClick";
import dayjs, { Dayjs } from "dayjs";
import DynamicDatesHelpDialog from "@/modules/explorer/components/DynamicDatesHelpDialog.vue";

const props = withDefaults(
	defineProps<{
		showHelp?: boolean;
		roundUp?: boolean;
	}>(),
	{
		showHelp: true,
		roundUp: false,
	}
);

const model = defineModel<string>();
const parsed = defineModel<Dayjs>("parsed", {
	default: () => ref(""),
});
const isInputValid = defineModel<boolean>("isInputValid", {
	default: () => ref(true),
});

watch(
	model,
	() => {
		parsed.value = parse(model.value, props.roundUp);
	},
	{ immediate: true }
);

const isDateValid = computed(() => {
	return isValid(parsed.value);
});

watch(isDateValid, (value) => {
	isInputValid.value = value;
});

const isDatePickerOpen = ref(false);
const datePickerMenu = ref();

useDetectOutsideClick(datePickerMenu, () => {
	nextTick(() => {
		isDatePickerOpen.value = false;
	});
});

const datePickerValue = computed(() => {
	if (!isDateValid.value) {
		return null;
	}
	return parsed.value.toDate();
});

const updateDatePickerValue = (date) => {
	model.value = dayjs(date).format("YYYY-MM-DD HH:mm:ss");
};

/**
 * The field itself holds the stored value — `YYYY-MM-DD HH:mm:ss`, or a dynamic
 * expression like `now-7d` — because that is what `parse()` and the backend
 * read. The hint renders whatever that resolves to in the app's usual date
 * format, so a concrete date is confirmed and a dynamic one is made concrete.
 */
const DISPLAY_FORMAT = "DD MMM YYYY HH:mm";

const formattedParsed = computed(() =>
	isDateValid.value && parsed.value
		? parsed.value.format(DISPLAY_FORMAT)
		: undefined
);
const showDynamicDatesHelpDialog = ref(false);
</script>

<template>
	<dynamic-dates-help-dialog v-model="showDynamicDatesHelpDialog" />

	<div class="date-field">
		<v-text-field
			variant="filled"
			density="compact"
			hide-details="auto"
			v-model="model"
			:error-messages="isDateValid ? [] : 'Fill in a valid date or now'"
			persistent-hint
			:hint="formattedParsed"
		>
			<template v-slot:append-inner>
				<v-icon size="18" @click.stop="isDatePickerOpen = !isDatePickerOpen"
					>mdi-calendar</v-icon
				>
			</template>
			<template v-if="props.showHelp" v-slot:append>
				<v-icon size="18" @click.stop="showDynamicDatesHelpDialog = true"
					>mdi-help-circle-outline</v-icon
				>
			</template>
		</v-text-field>
		<div v-if="isDatePickerOpen" ref="datePickerMenu" class="datePickerMenu">
			<vc-date-picker
				is24hr
				hide-time-header
				show-iso-weeknumbers
				mode="dateTime"
				style="width: 100%"
				:first-day-of-week="2"
				:popover="{ placement: 'auto' }"
				:attributes="[{ key: 'today', dot: true, dates: new Date() }]"
				:model-value="datePickerValue"
				@update:modelValue="updateDatePickerValue"
			>
				<template #footer>
					<div class="w-100 d-flex align-center justify-center pb-0"></div>
				</template>
			</vc-date-picker>
		</div>
	</div>
</template>

<style scoped lang="scss">
/*
 * Wide enough for a full timestamp plus the calendar and help icons, so the
 * field never collapses to the point of clipping a dynamic expression.
 */
.date-field {
	min-width: 260px;
}
</style>
